import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { ArrowLeft, MapPin, Calendar, Package, Truck, User, Mail, Phone, Edit, Pencil, Download, FileText, AlertTriangle } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import TransportMap from '../components/TransportMap';
import StatusTimelineSimplified from '../components/StatusTimelineSimplified';
import DocumentsChecklist from '../components/DocumentsChecklist';
import OperationDocumentsSection from '../components/OperationDocumentsSection';
import ClientLoadingNotesSection from '../components/operations/ClientLoadingNotesSection';
import PodValidationBanner from '../components/PodValidationBanner';
import MessagingSystem from '../components/MessagingSystem';
import NegotiationPanelV2 from '../components/NegotiationPanelV2';
import LogisticsInfoCard from '../components/LogisticsInfoCard';
import WorkflowPanel from '../components/WorkflowPanel';
import VotreInterlocuteur from '../components/VotreInterlocuteur';
import OperationNotificationBanner from '../components/OperationNotificationBanner';
import { OperationSupportFab } from '../components/operations/OperationSupportFab';
import { ClientCompactWorkflow } from '../components/workflow/CompactDualWorkflow';
import ActionsClient from '../components/workflow/ActionsClient';
import QuoteValidationModal from '../components/workflow/QuoteValidationModal';
import EditOperationModal from '../components/EditOperationModal';
import StickyHeader from '../components/operation/StickyHeader';
import LogisticBlock from '../components/operation/LogisticBlock';
import SidebarRight from '../components/operation/SidebarRight';
import IncidentsPanel from '../components/IncidentsPanel';
import { useOperationDocuments } from '../hooks/useOperationDocuments';
import usePermissions from '../contexts/PermissionsContext';
import DocumentsComplianceCenter from '../components/operations/documents/DocumentsComplianceCenter';
import DocumentsComplianceRail from '../components/operations/documents/DocumentsComplianceRail';
import AddDocumentModal from '../components/operations/documents/AddDocumentModal';
import RequestFieldModal from '../components/operations/documents/RequestFieldModal';
import axios from 'axios';
import { toast } from '../hooks/use-toast';
import { getStatusBadge as getStatusConfig, getStatusLabel } from '../constants/statuses';
import { useAuth } from '../contexts/AuthContext';
import { formatVehicleType, formatOperationType, formatMerchandiseType, formatDate } from '../utils/formatters';
import ExternalRefField from '../components/common/ExternalRefField';
import { logger } from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OperationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [operation, setOperation] = useState(null);
  const [previousStatus, setPreviousStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [childOperations, setChildOperations] = useState([]);
  const [hasSignedUnloading, setHasSignedUnloading] = useState(false);
  const [showQuoteValidationModal, setShowQuoteValidationModal] = useState(false);
  const [showEditOperationModal, setShowEditOperationModal] = useState(false);

  // Onglet Documents — centre de conformité (staff)
  const [activeTab, setActiveTab] = useState('timeline');
  const isPrivileged = ['admin', 'ops', 'lead_ops', 'operateur', 'commercial', 'lead_commercial', 'field_ops', 'lead_field_ops'].includes(user?.role);
  const { hasPermission } = usePermissions();
  const docData = useOperationDocuments(isPrivileged ? id : null);
  const [addDocState, setAddDocState] = useState({ open: false, type: 'autre', files: [] });
  const [reqFieldState, setReqFieldState] = useState({ open: false, labels: [] });
  const openAddDoc = (type = 'autre', files = []) => setAddDocState({ open: true, type, files: files ? Array.from(files) : [] });
  const openRequestField = (labels = []) => setReqFieldState({ open: true, labels: labels && labels.length ? labels : docData.stats.manquantsList });

  // Statuts permettant la modification par le client (uniquement phase initiale)
  const MODIFIABLE_STATUSES_CLIENT = [
    'nouvelle',
    'demande_enregistree'
  ];
  const canModifyOperation = operation && MODIFIABLE_STATUSES_CLIENT.includes(operation.status);

  const fetchOperation = async () => {
    try {
      const response = await axios.get(`${API}/transport-requests/${id}`);
      const newOperation = response.data;
      
      // Auto-redirect if this is a contract run (not a regular operation)
      if (newOperation.isContractRun && newOperation.contractId) {
        navigate(`/mes-contrats/${newOperation.contractId}/transferts/${newOperation.id}`, { replace: true });
        return;
      }
      
      if (previousStatus && previousStatus !== newOperation.status) {
        toast({
          title: "Mise à jour",
          description: `Votre opération est maintenant "${getStatusLabel(newOperation.status)}"`,
          duration: 4000
        });
      }
      
      setPreviousStatus(newOperation.status);
      setOperation(newOperation);
      setLoading(false);
      
      // Fetch child operations (transferts)
      try {
        const childRes = await axios.get(`${API}/operations/${id}/children`);
        setChildOperations(childRes.data?.items || []);
      } catch { /* no children */ }

      // Check unloading signed note presence (for POD banner)
      try {
        const notesRes = await axios.get(`${API}/operations/${id}/loading-notes`);
        const items = Array.isArray(notesRes.data) ? notesRes.data : (notesRes.data?.items || []);
        setHasSignedUnloading(items.some(n =>
          n.type === 'unloading' &&
          (n.status === 'signed' || (Array.isArray(n.signatures) && n.signatures.length >= 2))
        ));
      } catch { /* noop */ }
    } catch (error) {
      console.error('Error fetching operation:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger l'opération",
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOperation();
    
    // Auto-refresh every 2 seconds for near real-time updates
    const intervalId = setInterval(() => {
      fetchOperation();
    }, 2000); // Rafraîchissement toutes les 2 secondes (plus réactif)
    
    // Cleanup interval on unmount
    return () => clearInterval(intervalId);
  }, [id]);

  const generatePDF = async (type) => {
    try {
      toast({
        title: "⏳ Génération en cours...",
        description: `Génération du ${type === 'quote' ? 'devis' : 'facture'}...`
      });

      // Endpoint CLIENT dédié (ownership company-scopé, réutilise PDFService + version FNE certifiée).
      // NB: ne pas appeler /documents/generate/{type}/{id} (perm staff finances.create_invoice -> 403).
      const response = await axios.get(
        `${API}/client/finances/documents/${type}_${id}/pdf`,
        { responseType: 'blob' }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const docType = type === 'quote' ? 'Devis' : 'Facture';
      link.setAttribute('download', `${docType}_${operation.id.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast({
        title: "✅ Document généré",
        description: `Le ${docType.toLowerCase()} a été téléchargé avec succès`
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: "Erreur",
        description: "Impossible de générer le document",
        variant: "destructive"
      });
    }
  };

  const downloadLoadingNotePDF = async (note) => {
    try {
      toast({ title: "⏳ Téléchargement en cours..." });
      const response = await axios.get(
        `${API}/operations/${id}/loading-notes/${note.id}/pdf`,
        { responseType: 'blob', headers: { 'Authorization': 'Bearer cookie-auth' } }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${note.noteNumber || 'bon'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: "✅ Bon téléchargé", description: note.noteNumber });
    } catch (error) {
      console.error('Error downloading loading note PDF:', error);
      toast({
        title: "Erreur",
        description: "Impossible de télécharger ce bon",
        variant: "destructive"
      });
    }
  };

  const getStatusBadge = (status) => {
    const config = getStatusConfig(status);
    return (
      <Badge className={`${config.bgColor} ${config.textColor} border-0 text-sm`}>
        {config.icon} {config.label}
      </Badge>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    return price ? `${price.toLocaleString('fr-FR')} XOF` : 'N/A';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1db584]"></div>
      </div>
    );
  }

  if (!operation) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Opération non trouvée</p>
        <Button onClick={() => navigate('/operations')} className="mt-4">
          Retour aux opérations
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sticky Header */}
      <StickyHeader
        operation={operation}
        onBack={() => navigate(-1)}
        getStatusBadge={getStatusBadge}
        formatPrice={formatPrice}
      />

      {/* Timeline des Workflows (Opération + Finance) - Vue Client Compacte */}
      <div className="container mx-auto px-4 py-4">
        <ClientCompactWorkflow 
          operation={operation}
          onStageClick={(stage) => {
            logger.debug('Stage clicked:', stage);
          }}
        />
        
        {/* Notification Banner for Action Required */}
        <div className="mt-4">
          <OperationNotificationBanner
            operationId={operation.id}
            userId={user?.id}
            onActionComplete={fetchOperation}
          />
        </div>
      </div>

      {/* Main Layout: 2 Columns */}
      <div className="container mx-auto px-4 pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Main Content */}
          <div className="lg:col-span-2 space-y-4">
            {/* Logistic Block: Map + Operation Details */}
            <LogisticBlock
              operation={operation}
              formatVehicleType={formatVehicleType}
              formatOperationType={formatOperationType}
              formatMerchandiseType={formatMerchandiseType}
              formatDate={formatDate}
            />

            {/* LOT B — Référence externe client */}
            <Card>
              <CardContent className="py-4">
                <ExternalRefField operation={operation} isStaff={user?.role !== 'client'} onUpdated={fetchOperation} />
              </CardContent>
            </Card>

            {/* Bandeau POD client — Phase 3b (visible client uniquement) */}
            {user?.role === 'client' && (
              <PodValidationBanner
                targetKind="operation"
                operationId={operation.id}
                label={operation.operationNumber || `Op #${operation.id?.slice(0, 8)}`}
                clientValidation={operation.clientValidation}
                hasSignedUnloadingNote={hasSignedUnloading}
                onChange={fetchOperation}
              />
            )}

            {/* Tabs Section */}
            <Card>
              <CardContent className="pt-6">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <div className="relative">
                    <TabsList
                      className="flex w-full justify-start overflow-x-auto overflow-y-hidden gap-1 px-1 h-auto py-1 [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent scroll-smooth"
                      data-testid="op-detail-tabs"
                    >
                      <TabsTrigger value="timeline" className="shrink-0 whitespace-nowrap px-3">Timeline</TabsTrigger>
                      <TabsTrigger value="workflow" className="shrink-0 whitespace-nowrap px-3">Workflow</TabsTrigger>
                      <TabsTrigger value="logistics" className="shrink-0 whitespace-nowrap px-3">Logistique</TabsTrigger>
                      <TabsTrigger value="documents" className="shrink-0 whitespace-nowrap px-3">Documents</TabsTrigger>
                      <TabsTrigger value="bons" data-testid="tab-bons-signatures" className="shrink-0 whitespace-nowrap px-3">Bons &amp; signatures</TabsTrigger>
                      {childOperations.length > 0 && (
                        <TabsTrigger value="transfers" className="shrink-0 whitespace-nowrap px-3">
                          Transferts ({childOperations.length})
                        </TabsTrigger>
                      )}
                      <TabsTrigger value="incidents" className="shrink-0 whitespace-nowrap px-3 relative">
                        <AlertTriangle className="w-4 h-4 mr-1" />
                        Signaler
                        {operation?.has_incident && (
                          <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                        )}
                      </TabsTrigger>
                      <TabsTrigger value="messages" className="shrink-0 whitespace-nowrap px-3">Messages</TabsTrigger>
                    </TabsList>
                    <div className="pointer-events-none absolute right-0 top-0 h-full w-8 bg-gradient-to-l from-white to-transparent" />
                  </div>
                  
                  <TabsContent value="timeline" className="mt-6">
                    <StatusTimelineSimplified 
                      currentStatus={operation.status}
                      statusHistory={{
                        pending: operation.createdAt,
                      }}
                    />
                  </TabsContent>
                  
                  <TabsContent value="workflow" className="mt-6">
                    <WorkflowPanel
                      operation={operation}
                      onStatusChange={fetchOperation}
                    />
                  </TabsContent>
                  
                  <TabsContent value="logistics" className="mt-6">
                    <LogisticsInfoCard
                      operationId={operation.id}
                      operation={operation}
                    />
                  </TabsContent>
                  
                  <TabsContent value="documents" className="mt-6 space-y-6">
                    {isPrivileged ? (
                      <DocumentsComplianceCenter
                        operationId={operation.id}
                        operation={operation}
                        docData={docData}
                        onAddDoc={openAddDoc}
                        onRequestField={openRequestField}
                        hideSectionD
                        canValidate={hasPermission('operations.validate_documents')}
                      />
                    ) : (
                      <>
                        {/* Documents commerciaux : Devis + Facture (auto-générés) */}
                        <OperationDocumentsSection
                          operationId={operation.id}
                          operation={operation}
                        />

                        {/* Documents véhicule + chauffeur (visible si uploadé côté admin) */}
                        <DocumentsChecklist
                          operationId={operation.id}
                          userRole={user?.role}
                        />
                      </>
                    )}
                  </TabsContent>

                  <TabsContent value="bons" className="mt-6 space-y-6">
                    {/* Bons de chargement / déchargement signés (PM Spec 30/04/2026) */}
                    <ClientLoadingNotesSection operationId={operation.id} />
                  </TabsContent>
                  
                  {/* Dedicated Transfers tab */}
                  {childOperations.length > 0 && (
                    <TabsContent value="transfers" className="mt-6">
                      <div className="space-y-2">
                        <h3 className="text-sm font-semibold text-gray-700 mb-3">
                          Transferts ({childOperations.length})
                        </h3>
                        {childOperations.map(child => {
                          const stops = child.stops || [];
                          const dep = stops[0]?.address?.split(',')[0] || child.departure?.split(',')[0] || '-';
                          const arr = stops.length > 1 ? stops[stops.length - 1]?.address?.split(',')[0] : (child.arrival?.split(',')[0] || '-');
                          return (
                            <div
                              key={child.id}
                              className="flex items-center justify-between p-3 bg-white rounded-lg border cursor-pointer hover:bg-zinc-50 hover:border-zinc-300 transition-colors"
                              data-testid={`child-transfer-${child.id}`}
                              onClick={() => navigate(`/mes-contrats/${child.contractId}/transferts/${child.id}`)}
                            >
                              <div className="flex items-center gap-3">
                                <span className="text-xs font-mono text-gray-500">{child.runNumber || child.operationNumber}</span>
                                <span className="text-sm">{dep} &rarr; {arr}</span>
                                {child.plannedDriverName && <span className="text-xs text-zinc-400">| {child.plannedDriverName}</span>}
                                {child.date && <span className="text-xs text-zinc-400">| {new Date(child.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}</span>}
                              </div>
                              <Badge variant={child.status === 'completed' || child.status === 'terminee' ? 'default' : 'secondary'} className="text-xs">
                                {child.status}
                              </Badge>
                            </div>
                          );
                        })}
                      </div>
                    </TabsContent>
                  )}

                  <TabsContent value="incidents" className="mt-6">
                    <IncidentsPanel
                      operationId={operation.id || id}
                      operationReference={operation.reference}
                      token={'cookie-auth'}
                    />
                  </TabsContent>
                  
                  <TabsContent value="messages" className="mt-6">
                    <MessagingSystem
                      operationId={operation.id}
                      assignedOperator={
                        operation.assignedOperatorName ? {
                          id: operation.assignedOperatorId,
                          name: operation.assignedOperatorName
                        } : null
                      }
                      assignedFieldOps={
                        operation.assignedFieldOpsName ? {
                          id: operation.assignedFieldOpsId,
                          name: operation.assignedFieldOpsName
                        } : null
                      }
                    />
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          {/* Right Sidebar */}
          {activeTab === 'documents' && isPrivileged ? (
            <DocumentsComplianceRail
              stats={docData.stats}
              operation={operation}
              onShowMissing={() => { window.dispatchEvent(new Event('op-docs-show-missing')); document.getElementById('op-docs-sections')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
              onRequestField={() => openRequestField(docData.stats.manquantsList)}
              onImportMultiple={() => openAddDoc('autre')}
              onHistory={() => document.getElementById('op-docs-recent')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            />
          ) : (
          <SidebarRight
            operation={operation}
            onSendMessage={(recipient) => {
              // Store selected recipient in localStorage for MessagingSystem
              if (recipient) {
                localStorage.setItem('selected_message_recipient', JSON.stringify(recipient));
              }
              
              // Navigate to messages tab by clicking the TabsTrigger
              const messagesTab = document.querySelector('button[value="messages"]');
              if (messagesTab) {
                messagesTab.click();
                // Scroll to tabs section
                messagesTab.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }}
            ActionsComponent={
              <>
                {/* 1. Tarification (en premier) */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Tarification</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div>
                      <p className="text-xs text-gray-500">
                        {operation.finalPrice && operation.finalPrice !== operation.estimatedPrice ? 'Prix convenu' : 'Prix estimé'}
                      </p>
                      <p className="text-2xl font-bold text-[#1db584] mt-2">
                        {(operation.finalPrice || operation.estimatedPrice)?.toLocaleString('fr-FR')} XOF
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* 2. Client Actions (Vos actions) */}
                <ActionsClient
                  operation={operation}
                  onValidateQuote={() => setShowQuoteValidationModal(true)}
                  onDownloadQuote={() => generatePDF('quote')}
                  onDownloadInvoice={() => generatePDF('invoice')}
                  onViewDetails={() => logger.debug('View details')}
                />

                {/* 3. Modifier l'opération (si autorisé) */}
                {canModifyOperation && (
                  <Card className="border-blue-200 bg-blue-50/50">
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-blue-900">Modifier l'opération</p>
                          <p className="text-xs text-blue-600">Ajustez les détails de votre demande</p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowEditOperationModal(true)}
                          className="bg-white border-blue-300 text-blue-700 hover:bg-blue-100"
                          data-testid="client-edit-operation-btn"
                        >
                          <Pencil className="w-4 h-4 mr-2" />
                          Modifier
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            }
          >
            {/* 3. Votre Interlocuteur (dans le children après ActionsComponent) */}
            {/* Sera affiché automatiquement par SidebarRight si assignedOperatorName existe */}

            {/* 4. Client Information (Vos informations - en dernier) */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <User className="w-4 h-4 text-[#1db584]" />
                  Vos Informations
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div>
                  <p className="text-xs text-gray-500">Nom complet</p>
                  <p className="font-medium text-sm">{operation.firstName} {operation.lastName}</p>
                </div>
                <Separator />
                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Mail className="w-3 h-3 text-gray-400" />
                    <a href={`mailto:${operation.email}`} className="text-[#1db584] hover:underline text-xs">
                      {operation.email}
                    </a>
                  </div>
                </div>
                <Separator />
                <div>
                  <p className="text-xs text-gray-500">Téléphone</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Phone className="w-3 h-3 text-gray-400" />
                    <a href={`tel:${operation.phone}`} className="text-[#1db584] hover:underline text-xs">
                      {operation.phone}
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </SidebarRight>
          )}
        </div>
      </div>

      {/* Modales documents (centre de conformité) */}
      {isPrivileged && (
        <>
          <AddDocumentModal
            open={addDocState.open}
            onOpenChange={(v) => setAddDocState((s) => ({ ...s, open: v }))}
            operationId={operation.id}
            defaultType={addDocState.type}
            initialFiles={addDocState.files}
            onUploaded={docData.refetch}
          />
          <RequestFieldModal
            open={reqFieldState.open}
            onOpenChange={(v) => setReqFieldState((s) => ({ ...s, open: v }))}
            operationId={operation.id}
            missingLabels={reqFieldState.labels}
          />
        </>
      )}

      {/* Modal de Validation de Devis */}
      <QuoteValidationModal
        isOpen={showQuoteValidationModal}
        onClose={() => setShowQuoteValidationModal(false)}
        operation={operation}
        onSubmit={async (data) => {
          try {
            let newStatus = null;
            let reason = '';
            
            // Déterminer la transition selon l'action (Workflow V2)
            if (data.action === 'accept') {
              newStatus = 'devis_accepte';  // Workflow V2: étape intermédiaire
              reason = 'Devis accepté par le client';
            } else if (data.action === 'refuse') {
              newStatus = 'annulee_par_client';
              reason = 'Devis refusé par le client';
            } else if (data.action === 'negotiate') {
              newStatus = 'en_negociation';
              reason = `Client propose ${data.proposedPrice} FCFA. Message: ${data.message}`;
              
              // Stocker la proposition de négociation
              await axios.post(
                `${API}/transport-requests/${operation.id}/negotiate`,
                {
                  proposedPrice: data.proposedPrice,
                  message: data.message
                },
                {
                  headers: { 'Authorization': 'Bearer cookie-auth' }
                }
              );
              // L'endpoint /negotiate change déjà le statut, pas besoin de le faire à nouveau
              newStatus = null;  // Skip status change for negotiation
            }
            
            // Changer le statut de l'opération (seulement pour accept/refuse)
            if (newStatus) {
              await axios.post(
                `${API}/operation-status/${operation.id}/change`,
                {
                  new_status: newStatus,
                  reason: reason
                },
                {
                  headers: { 'Authorization': 'Bearer cookie-auth' }
                }
              );
            }
            
            toast({
              title: data.action === 'accept' ? '✅ Devis accepté' :
                     data.action === 'refuse' ? '❌ Devis refusé' :
                     '💬 Proposition envoyée',
              description: data.action === 'accept' ? 'Votre opération va être planifiée' :
                          data.action === 'refuse' ? 'L\'opération a été annulée' :
                          'Votre proposition a été envoyée à l\'équipe commerciale'
            });
            
            await fetchOperation();
          } catch (error) {
            throw new Error(error.response?.data?.detail || error.message || "Erreur lors de l'enregistrement");
          }
        }}
      />

      {/* Modal de Modification de l'Opération */}
      <EditOperationModal
        isOpen={showEditOperationModal}
        onClose={() => setShowEditOperationModal(false)}
        operation={operation}
        userRole="client"
        onSuccess={(updatedOperation) => {
          setOperation(updatedOperation);
          setShowEditOperationModal(false);
          fetchOperation();
        }}
      />

      {/* FAB Support contextuel */}
      <OperationSupportFab operation={operation} />
    </div>
  );
};

export default OperationDetail;
